// src/fleet-stats/components/RobotHealthSection.jsx
import React, { useState, useMemo } from "react";
import { Activity, Info } from "lucide-react";
import { C } from "../constants.js";
import { DataTable } from "./SharedUI.jsx";
import { fmtNum } from "../utils/format.js";

// RRI bands in display order — most actionable first
const RRI_FILTER_BANDS = [
    { key: "at-risk-below", label: "Needs attention", match: (r) => r.rri !== null && r.rri < 40, color: "#ff8c5f" },
    { key: "all",           label: "All robots",      match: () => true,                           color: C.grey1  },
    { key: "optimal",       label: "Optimal",         match: (r) => r.rri !== null && r.rri >= 80, color: "#7bff89" },
    { key: "operational",   label: "Operational",     match: (r) => r.rri !== null && r.rri >= 60 && r.rri < 80, color: "#a8e063" },
    { key: "underperform",  label: "Underperforming", match: (r) => r.rri !== null && r.rri >= 40 && r.rri < 60, color: "#f5a623" },
    { key: "at-risk",       label: "At risk",         match: (r) => r.rri !== null && r.rri >= 20 && r.rri < 40, color: "#ff8c5f" },
    { key: "inactive",      label: "Inactive",        match: (r) => r.rri !== null && r.rri < 20,  color: "#ff5f5f" },
    { key: "insufficient",  label: "Insufficient data", match: (r) => r.rri === null,              color: C.grey2  },
];

function RRIBadge({ rri, label, color, estimated }) {
    return (
        <div className="flex items-center gap-1.5">
            <span
                className="text-xs font-semibold px-2 py-0.5 rounded-full"
                style={{
                    backgroundColor: `${color}18`,
                    color,
                    border: `1px solid ${color}44`,
                }}
            >
                {label}
                {rri !== null && <span className="ml-1 opacity-70">{rri}</span>}
            </span>
            {estimated && (
                <span
                    className="text-xs px-1.5 py-0.5 rounded-full"
                    style={{ backgroundColor: "rgba(156,174,179,0.1)", color: C.grey2, border: "1px solid rgba(156,174,179,0.2)" }}
                    title="Score estimated from site peers — robot has less than 21 days of its own history"
                >
                    est.
                </span>
            )}
        </div>
    );
}

function ExplanationTooltip({ text }) {
    const [show, setShow] = useState(false);
    return (
        <div className="relative inline-flex">
            <button
                onMouseEnter={() => setShow(true)}
                onMouseLeave={() => setShow(false)}
                style={{ color: C.grey2, lineHeight: 0 }}
            >
                <Info size={13} />
            </button>
            {show && (
                <div
                    className="absolute z-50 bottom-full mb-2 left-0"
                    style={{
                        backgroundColor: C.deepBlue,
                        border: `1px solid ${C.grey3}`,
                        borderRadius: 8,
                        padding: "8px 12px",
                        color: C.grey1,
                        fontSize: 12,
                        lineHeight: 1.5,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
                        width: 280,
                        whiteSpace: "normal",
                    }}
                >
                    {text}
                </div>
            )}
        </div>
    );
}

export function RobotHealthSection({ perRobot, healthMap, onSelectRobot = null }) {
    const [open, setOpen] = useState(false);
    // "All" default — this is now the page's single all-robots table; the
    // attention section already covers the needs-attention view.
    const [filterKey, setFilterKey] = useState("all");

    const enriched = useMemo(() => {
        return perRobot.map((r) => {
            const h = healthMap.get(r.robotId);
            return {
                ...r,
                rri:              h?.rri ?? null,
                rriLabel:         h?.label ?? "Insufficient data",
                rriColor:         h?.color ?? C.grey2,
                estimated:        h?.estimated ?? false,
                consistencyScore: h?.consistencyScore ?? null,
                utilizationScore: h?.utilizationScore ?? null,
                stabilityScore:   h?.stabilityScore ?? null,
                utilization:      h?.utilization ?? null,
                kmPerUptimeH:     h?.kmPerUptimeH ?? null,
                kmPerDrivingH:    h?.kmPerDrivingH ?? null,
                explanation:      h?.explanation ?? "",
            };
        });
    }, [perRobot, healthMap]);

    const counts = useMemo(() => {
        const c = {};
        for (const band of RRI_FILTER_BANDS) {
            c[band.key] = enriched.filter(band.match).length;
        }
        return c;
    }, [enriched]);

    const activeBand = RRI_FILTER_BANDS.find((b) => b.key === filterKey) || RRI_FILTER_BANDS[0];
    const filtered = enriched.filter(activeBand.match)
        .sort((a, b) => {
            // Sort: nulls last, then ascending RRI (worst first)
            if (a.rri === null && b.rri === null) return 0;
            if (a.rri === null) return 1;
            if (b.rri === null) return -1;
            return a.rri - b.rri;
        });

    return (
        <div className="mb-8">
            {/* Section toggle */}
            <button
                onClick={() => setOpen((v) => !v)}
                className="inline-flex items-center gap-2 text-sm font-semibold mb-1 transition-colors"
                style={{ color: C.grey1 }}
                onMouseEnter={(e) => (e.currentTarget.style.color = C.neonGreen)}
                onMouseLeave={(e) => (e.currentTarget.style.color = C.grey1)}
            >
                <Activity size={16} />
                {open ? "Hide" : "Show"} all robots — activity &amp; readiness ({enriched.length})
            </button>

            {/* Always-visible description */}
            <div className="text-xs mb-3" style={{ color: C.grey2 }}>
                RRI scores each robot 0–100 across three self-referential dimensions based on Bechtsis et al. (2022): <strong style={{ color: C.grey1 }}>Consistency</strong> — how closely recent driving matches the robot's own 3-week baseline (weekdays only); <strong style={{ color: C.grey1 }}>Utilization</strong> — proportion of powered-on time spent driving; <strong style={{ color: C.grey1 }}>Stability</strong> — how predictable the robot's daily activity is vs its own rolling average. No fleet-wide benchmark — each robot is compared only to itself.
            </div>

            {open && (
                <>
                    {/* Filter tabs */}
                    <div className="flex flex-wrap gap-2 mb-3">
                        {RRI_FILTER_BANDS.map((band) => {
                            const isActive = filterKey === band.key;
                            return (
                                <button
                                    key={band.key}
                                    onClick={() => setFilterKey(band.key)}
                                    className="px-3 py-1 rounded-lg text-xs font-semibold transition-colors"
                                    style={{
                                        backgroundColor: isActive ? `${band.color}18` : "transparent",
                                        color: isActive ? band.color : C.grey2,
                                        border: `1px solid ${isActive ? band.color + "55" : "rgba(255,255,255,0.08)"}`,
                                    }}
                                >
                                    {band.label} ({counts[band.key] || 0})
                                </button>
                            );
                        })}
                    </div>

                    <DataTable
                        columns={[
                            { key: "client", label: "Client" },
                            { key: "siteName", label: "Site" },
                            {
                                key: "robotName",
                                label: "Robot",
                                render: (r) => (
                                    <div>
                                        <div className="font-medium">{r.robotName}</div>
                                        <div className="text-xs" style={{ color: C.grey2 }}>{r.robotId}</div>
                                    </div>
                                ),
                            },
                            {
                                key: "rri",
                                label: "RRI",
                                render: (r) => (
                                    <div className="flex items-center gap-2">
                                        <RRIBadge rri={r.rri} label={r.rriLabel} color={r.rriColor} estimated={r.estimated} />
                                        {r.explanation && <ExplanationTooltip text={r.explanation} />}
                                    </div>
                                ),
                            },
                            {
                                key: "km",
                                label: "km",
                                align: "right",
                                render: (r) => fmtNum(r.distance_m / 1000, 1),
                            },
                            {
                                key: "hours",
                                label: "hours",
                                align: "right",
                                render: (r) => fmtNum(r.moving_time_s / 3600, 1),
                            },
                            {
                                key: "consistencyScore",
                                label: "Consistency",
                                align: "right",
                                render: (r) => r.consistencyScore !== null
                                    ? <span style={{ color: r.consistencyScore < 0.5 ? "#ff8c5f" : C.offWhite }}>{Math.round(r.consistencyScore * 100)}%</span>
                                    : <span style={{ color: C.grey2 }}>—</span>,
                            },
                            {
                                key: "utilizationScore",
                                label: "Utilization",
                                align: "right",
                                render: (r) => r.utilizationScore !== null
                                    ? <span style={{ color: r.utilizationScore < 0.1 ? "#f5a623" : C.offWhite }}>{Math.round(r.utilizationScore * 100)}%</span>
                                    : <span style={{ color: C.grey2 }}>—</span>,
                            },
                            {
                                key: "stabilityScore",
                                label: "Stability",
                                align: "right",
                                render: (r) => r.stabilityScore !== null
                                    ? <span style={{ color: r.stabilityScore < 0.6 ? "#f5a623" : C.offWhite }}>{Math.round(r.stabilityScore * 100)}%</span>
                                    : <span style={{ color: C.grey2 }}>—</span>,
                            },
                            {
                                key: "kmPerDrivingH",
                                label: "km / driving h",
                                align: "right",
                                render: (r) => r.kmPerDrivingH !== null ? fmtNum(r.kmPerDrivingH, 2) : "—",
                            },
                            {
                                key: "activeDays",
                                label: "Active days",
                                align: "right",
                            },
                            { key: "firstActive", label: "First", render: (r) => r.firstActive || "—" },
                            { key: "lastActive", label: "Last", render: (r) => r.lastActive || "—" },
                        ]}
                        rows={filtered.map((r) => ({ ...r, _key: r.robotId }))}
                        emptyMessage="No robots in this category."
                        maxVisibleRows={12}
                        onRowClick={onSelectRobot ? (r) => onSelectRobot(r.robotName) : null}
                    />
                </>
            )}
        </div>
    );
}